import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import { useTranslate } from "react-polyglot";
import first from "../../assets/ic_top-1.svg";
import second from "../../assets/ic_top-2.svg";
import third from "../../assets/ic_top-3.svg";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import SearchField from "../../SharedComponents/SearchField.jsx";
import UserInfoDialog from "./UserInfoDialog.jsx";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    padding: "35px 20px",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  title: {
    color: "#00213A",
    fontSize: 24,
    fontWeight: 500,
    marginBottom: 25,
    width: "100%",
    textAlign: "center",
  },
  pointsBox: {
    textAlign: "center",
  },
  myPoints: {
    fontSize: 20,
    marginBottom: 16,
    textAlign: "center",
  },
  pointsPeriod: {
    "& .MuiChip-label": {
      fontWeight: 500,
    },
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    border: "none",
  },
  searchField: {
    width: "100%",
    marginBottom: 15,

    [theme.breakpoints.up("sm")]: {
      width: "49%",
    },
  },
  tableMainContainer: {
    borderRadius: 16,
    border: "2px solid rgba(0, 0, 0, .08)",
    backgroundColor: "#fff",
  },
  tableHead: {
    backgroundColor: theme.palette.primary.dark,
    "&  .MuiTableCell-head": {
      color: theme.palette.background.default,
      fontWeight: 600,
    },
  },
  leadCell: {
    fontWeight: 900,
    fontSize: 24,
    color: theme.palette.primary.dark,
    lineHeight: "40px",
    width: 150,
  },
  leadCellText: {
    marginLeft: 7,
  },
  pointCell: {
    fontWeight: 700,
    fontSize: 24,
    color: theme.palette.primary.dark,
    width: 150,
  },
  tableBody: {
    "&  .MuiTableCell-root": {
      borderBottom: "none",
      paddingTop: 5,
      paddingBottom: 5,
    },
  },
  tableRow: {
    cursor: "pointer",
  },
  userTableRow: {
    cursor: "pointer",
    backgroundColor: fade(theme.palette.primary.main, 0.1),
  },
}));

/**
 * @returns {*}
 * @constructor
 */
const Leaderboard = () => {
  const classes = useStyles();
  const t = useTranslate();
  const { setLeaderboardFilters, filteredLeaderboard, userInfo, leaderboard, levelsAndReward } = useBaseData();
  const [searchTextValue, setSearchText] = useState("");
  const [userId, setUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [myPoints, setMyPoints] = useState(0);
  const [subjectPoints, setSubjectPoints] = useState(0);
  const searchTimeoutHandle = useRef(0);

  // update points
  useEffect(() => {
    if (levelsAndReward?.levels.length) {
      setMyPoints(
        levelsAndReward?.levels.reduce((acc, level) => {
          return (acc += level.havePoints);
        }, 0),
      );
      setSubjectPoints(
        levelsAndReward?.levels.reduce((acc, level) => {
          return (acc += level.points);
        }, 0),
      );
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboard, levelsAndReward?.levels]);

  /** @param {ReactEventTarget} target */
  const searchFieldHandler = ({ target }) => {
    const text = target.value;
    setSearchText(text);
    clearTimeout(searchTimeoutHandle.current);
    if (text !== "") {
      searchTimeoutHandle.current = setTimeout(() => {
        setLeaderboardFilters({ text });
      }, 600);
    } else {
      setLeaderboardFilters({ text });
    }
  };

  /** @param {Number} userId */
  const handleRowClick = (userId) => {
    if (!userInfo?.subject?.openUserInfoEnabled) {
      return;
    }
    setUserId(userId);
    setDialogOpen(true);
  };

  /** */
  const onDialogClose = () => {
    setDialogOpen(false);
  };

  if (!filteredLeaderboard) return null;

  return (
    <Box className={classes.wrapper}>
      <Typography className={classes.title}>
        {userInfo.subject.leaderboardPerDay ? t("leaderboardPerDay") : t("leaderboard")}
      </Typography>
      {userInfo.subject.leaderboardPerDay && (
        <Box mb={2}>
          <Typography className={classes.myPoints} variant="body2">
            {t("myPoints")}
          </Typography>
          <Box className={classes.pointsBox}>
            <Chip
              color="primary"
              className={classes.pointsPeriod}
              variant="outlined"
              label={`${myPoints}/${subjectPoints}`}
            />
          </Box>
        </Box>
      )}
      <Box className={classes.searchField}>
        <SearchField value={searchTextValue} onChange={searchFieldHandler} />
      </Box>
      <TableContainer className={classes.tableMainContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>№</TableCell>
              <TableCell>{t("name")}</TableCell>
              <TableCell>{t("points")}</TableCell>
              <TableCell>{t("coins")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {filteredLeaderboard.map((listMember) => (
              <TableRow
                key={listMember.id}
                hover
                className={listMember.id === userInfo.id ? classes.userTableRow : classes.tableRow}
                onClick={() => handleRowClick(listMember.id)}
              >
                <TableCell className={classes.leadCell}>
                  {listMember.leaderboardPlace === 1 && <img src={first} alt="first" />}
                  {listMember.leaderboardPlace === 2 && <img src={second} alt="second" />}
                  {listMember.leaderboardPlace === 3 && <img src={third} alt="third" />}
                  {listMember.leaderboardPlace > 3 && (
                    <span className={classes.leadCellText}>{listMember.leaderboardPlace}</span>
                  )}
                </TableCell>
                <TableCell>{listMember.name}</TableCell>
                <TableCell className={classes.pointCell}>{listMember.points}</TableCell>
                <TableCell className={classes.pointCell}>
                  {/* Монеты за миссии могут быть дробными, отображаем округлённо до целого */}
                  {Math.round(listMember.coins)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <UserInfoDialog open={dialogOpen} onClose={onDialogClose} userId={userId} />
    </Box>
  );
};

export default Leaderboard;
